import React, { useState, useEffect } from "react";
import {
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Divider,
  Snackbar,
  Alert,
  Tooltip,
  Skeleton,
} from "@mui/material";
import {
  CalendarToday,
  Speed,
  Favorite,
  FavoriteBorder,
  LocationOn,
  KeyboardArrowLeft,
  KeyboardArrowRight,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useNavigationMemory from "../../hooks/useNavigationMemory";
import placeholderImage from "../../assets/image-placeholder.webp";
import { FavoriteButton } from "./FavoriteButton";
import ProductMenuButton from "./ProductMenuButton";
import DirectSaleModal from "../forms/DirectSaleModal";
import { useAuth, useFavorites } from "../../context/context";
import { SmartCardMedia } from "./SmartImage";

const ProductCard = ({
  product,
  onCardClick,
  mode = "normal",
  favoriteButton = true,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { saveScrollBeforeNavigation } = useNavigationMemory();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  // Estados para manejar clicks
  const [clickTimeout, setClickTimeout] = useState(null);
  const [clickCount, setClickCount] = useState(0);

  // Estado para la animación del corazón
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);

  // Estado para navegación de imágenes
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // 🎯 NUEVO: Estado para el modal de venta
  const [showSaleModal, setShowSaleModal] = useState(false);

  const fallbackWord = "N/A";

  // Preparar array de imágenes
  const productImages =
    product.images && product.images.length > 0
      ? product.images
      : [product.image || placeholderImage];

  // Función para formatear kilometraje con puntos
  const formatKilometers = (kilometers) => {
    if (!kilometers || kilometers === fallbackWord || kilometers === "") {
      return fallbackWord;
    }

    // Extraer solo los números del string
    const numericValue = kilometers.toString().replace(/[^\d]/g, "");

    if (!numericValue || numericValue === "0") {
      return fallbackWord;
    }

    // Formatear con puntos como separadores de miles
    const formatted = parseInt(numericValue).toLocaleString("es-CL");

    // Agregar "km" al final
    return `${formatted} km`;
  };

  // 🆕 Función para formatear uso (kilometraje o horómetro)
  const formatUsage = (product) => {
    // 🎯 PRIORIDAD 1: Usar los nuevos campos unificados del backend
    if (product.usageDisplay) {
      return product.usageDisplay;
    }

    // 🎯 PRIORIDAD 2: Construir manualmente si tenemos usage y usageUnit
    if (product.usage && product.usageUnit) {
      const numericValue = product.usage.toString().replace(/[^\d]/g, "");
      if (numericValue && numericValue !== "0") {
        const formatted = parseInt(numericValue).toLocaleString("es-CL");
        const unit = product.usageUnit === "hr" ? "hrs" : "km";
        return `${formatted} ${unit}`;
      }
    }

    // 🎯 PRIORIDAD 3: Fallback al campo legacy "kilometers"
    if (product.kilometers) {
      return formatKilometers(product.kilometers);
    }

    return fallbackWord;
  };

  // 🆕 Función para obtener el ícono apropiado según el tipo de uso
  const getUsageIcon = (product) => {
    // Si tiene usageUnit, usarlo para determinar el ícono
    if (product.usageUnit === "hr") {
      return <CalendarToday sx={{ fontSize: 16, color: "#004b93" }} />; // Ícono de reloj para horómetro
    }
    // Por defecto, usar ícono de velocidad para kilometraje
    return <Speed sx={{ fontSize: 16, color: "#004b93" }} />;
  };

  // Funciones para navegación de imágenes
  const handlePreviousImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === 0 ? productImages.length - 1 : prev - 1,
    );
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev === productImages.length - 1 ? 0 : prev + 1,
    );
  };

  // Reset del índice de imagen cuando cambie el producto
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product.id]);

  // Función para obtener valores con fallback "No definido"
  const getValue = (value) => {
    if (
      value === null ||
      value === undefined ||
      value === "" ||
      value === fallbackWord
    ) {
      return fallbackWord;
    }
    return value;
  };

  // Preparar datos con valores por defecto
  const productData = {
    name: getValue(product.name),
    year: getValue(product.year),
    kilometers: getValue(product.kilometers),
    usageDisplay: getValue(product.usageDisplay),
    model: getValue(product.model),
    estado: getValue(product.estado), // ✅ CORREGIDO: usar estado directamente
    marca: getValue(product.marca || product.brand), // Add brand field
    price: getValue(product.price),
    formattedPrice: getValue(product.formattedPrice),
    priceIVA: product.hasPriceDetails ? "Valor a pagar" : product.priceIVA, // ✅ NO usar getValue para mantener null/undefined
    ubicacion: getValue(product.ubicacion),
    featured: product.featured || false,
  };

  // Función mejorada para manejar clicks simples y dobles
  const handleCardClick = (e) => {
    // Evitar conflicto con el botón de favoritos y menú
    if (
      e.target.closest(".favorite-button") ||
      e.target.closest(".MuiIconButton-root")
    ) {
      return;
    }

    setClickCount((prev) => prev + 1);

    if (clickTimeout) {
      clearTimeout(clickTimeout);
    }

    const timeout = setTimeout(() => {
      if (clickCount === 0) {
        // Click simple - navegar al detalle
        saveScrollBeforeNavigation(product.slug);

        if (onCardClick) {
          onCardClick();
        }
        navigate(`/producto/${product.slug}`);
      }
      setClickCount(0);
    }, 300);

    setClickTimeout(timeout);

    // Si es el segundo click (doble click)
    if (clickCount === 1) {
      clearTimeout(timeout);
      setClickCount(0);

      // Siempre mostrar la animación del corazón
      setShowHeartAnimation(true);
      setTimeout(() => setShowHeartAnimation(false), 1000);

      // Solo agregar a favoritos si no está ya en favoritos
      if (!isFavorite(product.id) && user) {
        toggleFavorite(product);
        setSnackbarMessage("❤️ Producto agregado a favoritos con doble click");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }

      // Pequeña animación visual de feedback
      e.currentTarget.style.transform = "scale(0.98)";
      setTimeout(() => {
        if (e.currentTarget) {
          e.currentTarget.style.transform = "scale(1)";
        }
      }, 150);
    }
  };

  // 🎯 NUEVO: Handler para abrir modal de venta
  const handleRegisterSale = (product) => {
    setShowSaleModal(true);
  };

  // 🎯 NUEVO: Handler cuando se registra una venta exitosamente
  const handleSaleCreated = (saleData) => {
    setSnackbarMessage("✅ ¡Venta registrada exitosamente!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);

    // Opcional: Recargar la página o actualizar el producto localmente
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  // Limpiar timeout cuando se desmonte el componente
  useEffect(() => {
    return () => {
      if (clickTimeout) {
        clearTimeout(clickTimeout);
      }
    };
  }, [clickTimeout]);

  return (
    <>
      <Card
        sx={{
          height: mode !== "minimal" ? "100%" : "auto",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
          borderRadius: 3,
          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          // 🎯 Border especial cuando está seleccionado
          border: product.is_sold
            ? "2px solid #d32f2f"
            : "1px solid rgba(25, 118, 210, 0.1)",
          cursor: "pointer",
          opacity: product.is_sold ? 0.85 : 1,
          WebkitTapHighlightColor: "transparent",
          WebkitTouchCallout: "none",
          WebkitUserSelect: "none",
          KhtmlUserSelect: "none",
          MozUserSelect: "none",
          msUserSelect: "none",
          userSelect: "none",
          "&:focus": {
            outline: "none",
          },
          "&:focus-visible": {
            outline: "none",
          },
          "@media (hover: hover)": {
            "&:hover": {
              transform: "translateY(-3px) scale(1.01)",
              boxShadow: product.is_sold
                ? "none"
                : "0 15px 20px rgba(25, 118, 210, 0.15)",
              borderColor: product.is_sold ? "#d32f2f" : "primary.main",
              "& .product-image": {
                transform: product.is_sold ? "none" : "scale(1.1)",
              },
            },
          },
          "@media (hover: none)": {
            "&:active": {
              transition: "transform 0.1s ease",
            },
          },
        }}
        onClick={handleCardClick}
      >
        {/* 🎯 NUEVO: Botón de menú de 3 puntitos (solo admins) */}
        <ProductMenuButton
          product={product}
          onRegisterSale={handleRegisterSale}
          externakSetSnackbarSeverity={setSnackbarSeverity}
          externalSetSnackbarMessage={setSnackbarMessage}
          externalSetSnackbarOpen={setSnackbarOpen}
        />

        {/* Header con título */}
        <Box
          sx={{
            background: product.is_sold
              ? "linear-gradient(135deg, #757575 0%, #424242 100%)"
              : "linear-gradient(135deg, #004b93 0%, #003366 100%)",
            color: "white",
            p: mode === "minimal" ? 1 : 2,
            position: "relative",
            minHeight: mode === "minimal" ? 48 : 76,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Badge VENDIDO en el header */}
          {product.is_sold && (
            <Chip
              label="VENDIDO"
              size="small"
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: "#d32f2f",
                color: "white",
                fontWeight: "bold",
                fontSize: "0.8rem",
                height: 24,
                zIndex: 3,
                boxShadow: "0 2px 8px #d32f2f50",
                animation: "soldPulse 2s ease-in-out infinite",
                "@keyframes soldPulse": {
                  "0%, 100%": {
                    transform: "scale(1)",
                  },
                  "50%": {
                    transform: "scale(1.05)",
                  },
                },
              }}
            />
          )}

          <Typography
            variant="h6"
            component="h6"
            sx={{
              fontWeight: "bold",
              fontSize: mode !== "minimal" ? "1.1rem" : "0.9rem",
              lineHeight: 1.2,
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              opacity: product.is_sold ? 0.7 : 1,
              width: "100%",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            <Tooltip title={productData.name} placement="top">
              <span>
                {product.category +
                  " " +
                  (productData.marca === fallbackWord ? "" : productData.marca)}
                {/* {productData.name.length > 30
                  ? `${productData.name.substring(
                      0,
                      mode === "minimal" ? 10 : 23
                    )}...`
                  : productData.name} */}
              </span>
            </Tooltip>
          </Typography>
        </Box>

        {/* Imagen del producto con hover para múltiples imágenes */}
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <SmartCardMedia
            component="img"
            height={mode === "minimal" ? "100" : "200"}
            width={mode === "minimal" ? "100" : "auto"}
            image={productImages[currentImageIndex]}
            alt={productData.name}
            title={productData.name}
            className="product-image"
            sx={{
              objectFit: "cover",
              backgroundColor: "#f5f5f5",
              transition: "transform 0.3s ease",
              filter: product.is_sold
                ? "grayscale(100%) brightness(0.8)"
                : "none",
              "&:hover": {
                transform: product.is_sold ? "none" : "scale(1.05)",
              },
            }}
          />

          {/* 🆕 OVERLAY SEMITRANSPARENTE para productos vendidos */}
          {product.is_sold && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.4)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
          )}

          {/* 🆕 MARCA DE AGUA DIAGONAL "VENDIDO" */}
          {product.is_sold && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%) rotate(-25deg)",
                zIndex: 2,
                pointerEvents: "none",
                textAlign: "center",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontWeight: "bold",
                  color: "#d32f2f",
                  textShadow: "0 0 20px rgba(0,0,0,0.8), 0 0 10px #d32f2f50",
                  fontSize: mode === "minimal" ? "2rem" : "3rem",
                  letterSpacing: "8px",
                  textTransform: "uppercase",
                  WebkitTextStroke: "2px white",
                  opacity: 0.95,
                }}
              >
                VENDIDO
              </Typography>
            </Box>
          )}

          {/* Animación del corazón estilo Instagram */}
          {showHeartAnimation && !product.is_sold && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                zIndex: 10,
                pointerEvents: "none",
                animation: "heartBurst 1s ease-out forwards",
                "@keyframes heartBurst": {
                  "0%": {
                    transform: "translate(-50%, -50%) scale(0)",
                    opacity: 0,
                  },
                  "15%": {
                    transform: "translate(-50%, -50%) scale(1.2)",
                    opacity: 1,
                  },
                  "30%": {
                    transform: "translate(-50%, -50%) scale(0.95)",
                    opacity: 1,
                  },
                  "45%": {
                    transform: "translate(-50%, -50%) scale(1.1)",
                    opacity: 1,
                  },
                  "80%": {
                    transform: "translate(-50%, -50%) scale(1)",
                    opacity: 1,
                  },
                  "100%": {
                    transform: "translate(-50%, -50%) scale(1.3)",
                    opacity: 0,
                  },
                },
              }}
            >
              <Favorite
                sx={{
                  fontSize: "4rem",
                  color: "#ff4569",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.3))",
                }}
              />
            </Box>
          )}
          {/* Indicador de producto destacado */}
          {product.featured && !product.is_sold && (
            <Chip
              label={mode === "minimal" ? "⭐" : "⭐ Destacado"}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                backgroundColor: "#d32f2f",
                color: "white",
                fontWeight: "bold",
                fontSize: "0.75rem",
                zIndex: 2,
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
            />
          )}

          {/* Corazón favorito - siempre en la esquina superior derecha de la imagen */}
          {favoriteButton && (
            <FavoriteButton
              product={product}
              externakSetSnackbarSeverity={setSnackbarSeverity}
              externalSetSnackbarMessage={setSnackbarMessage}
              externalSetSnackbarOpen={setSnackbarOpen}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 5,
                backgroundColor: "rgba(0,0,0,0.6)",
                backdropFilter: "blur(4px)",
                border: "2px solid rgba(255,255,255,0.3)",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.8)",
                  border: "2px solid rgba(255,255,255,0.5)",
                  transform: "scale(1.1)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
                },
                transition: "all 0.2s ease",
              }}
            />
          )}

          {/* Indicador de múltiples imágenes */}
          {product.images && product.images.length > 1 && !product.is_sold && (
            <Chip
              label={`${product.images.length} fotos`}
              size="small"
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                backgroundColor: "rgba(0,0,0,0.7)",
                color: "white",
                fontSize: "0.75rem",
              }}
            />
          )}
          {/* Flechas de navegación para imágenes - deshabilitar si está vendido */}
          {mode !== "minimal" &&
            product.images &&
            product.images.length > 1 &&
            !product.is_sold && (
              <>
                <IconButton
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 8,
                    zIndex: 2,
                    color: "white",
                    backgroundColor: "rgba(0,0,0,0.6)",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.8)",
                    },
                  }}
                  onClick={handlePreviousImage}
                >
                  <KeyboardArrowLeft />
                </IconButton>

                <IconButton
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 8,
                    zIndex: 2,
                    color: "white",
                    backgroundColor: "rgba(0,0,0,0.6)",
                    "&:hover": {
                      backgroundColor: "rgba(0,0,0,0.8)",
                    },
                  }}
                  onClick={handleNextImage}
                >
                  <KeyboardArrowRight />
                </IconButton>
              </>
            )}
        </Box>

        {/* Información del año y kilometraje */}
        {mode !== "minimal" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1.5,
              backgroundColor: product.is_sold ? "#e0e0e0" : "#f8f9fa",
            }}
          >
            <Box
              sx={{
                display: "flex",
                opacity:
                  !productData.year || productData.year !== fallbackWord
                    ? "1"
                    : "0",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <CalendarToday sx={{ fontSize: 16, color: "#004b93" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", color: "#004b93" }}
              >
                Año {productData.year}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                opacity:
                  productData.usageDisplay &&
                  productData.usageDisplay !== fallbackWord
                    ? "1"
                    : "0",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              {getUsageIcon(product)}
              <Typography
                variant="body2"
                sx={{ fontWeight: "bold", color: "#004b93" }}
              >
                {formatUsage(product)}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Contenido principal */}
        {mode !== "minimal" && (
          <CardContent sx={{ flexGrow: 1, p: 2 }}>
            {/* Información técnica */}
            <Box sx={{ mb: 2 }}>
              {/* MARCA */}
              <Box
                sx={{
                  opacity:
                    !productData.marca || productData.marca !== fallbackWord
                      ? "1"
                      : "0",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", minWidth: "80px" }}
                >
                  Marca:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Tooltip title={productData.marca} placement="top">
                    <span>
                      {productData.marca.length > 30
                        ? `${productData.marca.substring(0, 27)}...`
                        : productData.marca}
                    </span>
                  </Tooltip>
                </Typography>
              </Box>
              {/* MODELO */}
              <Box
                sx={{
                  opacity:
                    !productData.model || productData.model !== fallbackWord
                      ? "1"
                      : "0",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", minWidth: "80px" }}
                >
                  Modelo:
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <Tooltip title={productData.model} placement="top">
                    <span>
                      {productData.model.length > 30
                        ? `${productData.model.substring(0, 27)}...`
                        : productData.model}
                    </span>
                  </Tooltip>
                </Typography>
              </Box>
              {/* ESTADO */}
              <Box
                sx={{
                  opacity:
                    !productData.estado || productData.estado !== fallbackWord
                      ? "1"
                      : "0",
                  display: "flex",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "bold", minWidth: "80px" }}
                >
                  Estado:
                </Typography>
                <Chip
                  label={productData.estado}
                  size="small"
                  color={"red"}
                  sx={{ height: 20, fontSize: "0.75rem" }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Precio */}
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: "bold",
                  color: "#004b93",
                  fontSize: "1.5rem",
                }}
              >
                {productData.formattedPrice}
              </Typography>
              {/* Mostrar IVA solo si existe y no es vacío */}
              {productData.priceIVA &&
                productData.priceIVA !== fallbackWord &&
                productData.priceIVA.trim() !== "" && (
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{ fontWeight: "medium" }}
                  >
                    {productData.priceIVA}
                  </Typography>
                )}
            </Box>

            {/* Botón de acción - modificado para productos vendidos */}
            <Button
              variant="contained"
              fullWidth
              onClick={handleCardClick}
              disabled={product.is_sold}
              sx={{
                py: 1.5,
                fontWeight: "bold",
                borderRadius: 2,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                background: product.is_sold
                  ? "linear-gradient(135deg, #9e9e9e 0%, #757575 100%)"
                  : "linear-gradient(135deg, #004b93 0%, #003366 100%)",
                "&:hover": {
                  background: product.is_sold
                    ? "linear-gradient(135deg, #9e9e9e 0%, #757575 100%)"
                    : "linear-gradient(135deg, #003366 0%, #002244 100%)",
                },
                "&.Mui-disabled": {
                  color: "white",
                  opacity: 0.7,
                },
              }}
            >
              {product.is_sold ? "PRODUCTO VENDIDO" : "VER MÁS DETALLES"}
            </Button>

            {/* Ubicación */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                mt: 1.5,
                gap: 0.5,
                px: 1,
              }}
            >
              <LocationOn
                sx={{
                  fontSize: 18,
                  color: "#d32f2f",
                  mt: 0.2,
                  flexShrink: 0,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  textAlign: "center",
                  lineHeight: 1.3,
                  fontSize: "0.75rem",
                  textTransform: "capitalize",
                }}
              >
                {productData.ubicacion
                  .toLowerCase()
                  .split(", ")
                  .map((part, index, arr) => (
                    <span key={index}>
                      {index === 0 ? (
                        <strong style={{ color: "#333" }}>{part}</strong>
                      ) : (
                        part
                      )}
                      {index < arr.length - 1 && ", "}
                    </span>
                  ))}
              </Typography>
            </Box>
          </CardContent>
        )}
      </Card>

      {/* 🎯 NUEVO: Modal de registro de venta */}
      {showSaleModal && (
        <DirectSaleModal
          open={showSaleModal}
          onClose={() => setShowSaleModal(false)}
          product={product}
          onSaleCreated={handleSaleCreated}
        />
      )}

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductCard;
